import {
  documentNameMap,
  UNKNOWN_DOCUMENT_NAME,
} from 'app/configs/documentNameMap';

export const REVIEW_STATUS_OPTIONS = [
  'NOT_STARTED',
  'PROCESSING',
  'VALIDATION_FAILED',
  'USER_APPROVED',
  'NOT_APPLICABLE',
  // 'READY_TO_BE_SENT_TO_AGENT',
  'SENT_TO_AGENT_FOR_UPDATES',
  'READY_FOR_USER_REVIEW',
];

export const REVIEW_STATUS_OPTIONS_MAP = {
  NOT_STARTED: 'NOT_STARTED',
  PROCESSING: 'PROCESSING',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  READY_FOR_USER_REVIEW: 'READY_FOR_USER_REVIEW',
  MAX_APPROVED: 'MAX_APPROVED',
  USER_APPROVED: 'USER_APPROVED',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  READY_TO_BE_SENT_TO_AGENT: 'READY_TO_BE_SENT_TO_AGENT',
  SENT_TO_AGENT_FOR_UPDATES: 'SENT_TO_AGENT_FOR_UPDATES',
  AGENT_MAX_APPROVED: 'AGENT_MAX_APPROVED',
  AGENT_READY_FOR_REVIEW: 'AGENT_READY_FOR_REVIEW',
  AGENT_MAX_REVIEWING: 'AGENT_MAX_REVIEWING',
  AGENT_MAX_ERRORS_FOUND: 'AGENT_MAX_ERRORS_FOUND',
} as const satisfies Record<string, string>;

export type ReviewStatus = keyof typeof REVIEW_STATUS_OPTIONS_MAP;

// Unified status labels shared by the agent and compliance products, ≤3 words
// each. Single source of truth — every chip/badge/dropdown must read from here.
// Keep in sync with REVIEW_STATUS_LABELS in the backend (transactionDocumentService.js).
export const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  NOT_STARTED: 'Not Submitted',
  AGENT_MAX_ERRORS_FOUND: 'Errors Found',
  AGENT_MAX_APPROVED: 'Checks Passed',
  AGENT_READY_FOR_REVIEW: 'Ready to Submit',
  PROCESSING: 'Under Max Review',
  VALIDATION_FAILED: 'Errors Detected',
  READY_FOR_USER_REVIEW: 'Awaiting Processor Review',
  MAX_APPROVED: 'Max Approved',
  USER_APPROVED: 'Approved',
  SENT_TO_AGENT_FOR_UPDATES: 'Returned to Agent',
  NOT_APPLICABLE: 'Not Applicable',
  // Retired statuses (never written anymore) — kept so legacy data still renders.
  AGENT_MAX_REVIEWING: 'Under Max Review',
  READY_TO_BE_SENT_TO_AGENT: 'Awaiting Processor Review',
};

// Chip/badge colors per status, shared by StatusChip and DocumentStatusBadge.
export const REVIEW_STATUS_COLORS: Record<
  ReviewStatus,
  { bg: string; color: string }
> = {
  NOT_STARTED: { bg: '#EDEBEB', color: '#1A1A1A' },
  AGENT_MAX_ERRORS_FOUND: { bg: '#FFA8A8', color: '#1A1A1A' },
  AGENT_MAX_APPROVED: { bg: '#7FD6FF', color: '#1A1A1A' },
  AGENT_READY_FOR_REVIEW: { bg: '#E2FFA8', color: '#1A1A1A' },
  PROCESSING: { bg: '#FFD198', color: '#1A1A1A' },
  VALIDATION_FAILED: { bg: '#FFA8A8', color: '#1A1A1A' },
  READY_FOR_USER_REVIEW: { bg: '#7FD6FF', color: '#1A1A1A' },
  MAX_APPROVED: { bg: '#7FD6FF', color: '#1A1A1A' },
  USER_APPROVED: { bg: '#6CFF9D', color: '#1A1A1A' },
  SENT_TO_AGENT_FOR_UPDATES: { bg: '#FFD198', color: '#1A1A1A' },
  NOT_APPLICABLE: { bg: '#EDEBEB', color: '#1A1A1A' },
  AGENT_MAX_REVIEWING: { bg: '#FFD198', color: '#1A1A1A' },
  READY_TO_BE_SENT_TO_AGENT: { bg: '#7FD6FF', color: '#1A1A1A' },
};

const isReviewStatus = (status: string): status is ReviewStatus =>
  status in REVIEW_STATUS_LABELS;

// Known statuses get autocomplete; plain strings stay accepted because API
// payloads type status fields as string and legacy codes must still render.
export type ReviewStatusLike = ReviewStatus | (string & {});

export const getReviewStatusLabel = (
  status?: ReviewStatusLike | null
): string => {
  if (!status) return REVIEW_STATUS_LABELS.NOT_STARTED;
  if (isReviewStatus(status)) return REVIEW_STATUS_LABELS[status];
  return status.replace(/_/g, ' ');
};

export const getReviewStatusColors = (
  status?: ReviewStatusLike | null
): { bg: string; color: string } =>
  status && isReviewStatus(status)
    ? REVIEW_STATUS_COLORS[status]
    : REVIEW_STATUS_COLORS.NOT_STARTED;

// Human list phrase for eligibility copy, e.g. "Approved, Ready to Submit, or
// Not Applicable" (labels deduped since some statuses share one). `getLabel`
// lets partner-aware callers resolve legacy labels instead.
export const formatReviewStatusesPhrase = (
  statuses: readonly string[],
  getLabel: (status?: string | null) => string = getReviewStatusLabel
): string => {
  const parts = [...new Set(statuses.map((s) => getLabel(s)))];
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} or ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, or ${parts[parts.length - 1]}`;
};

// displayStatus values counted as incomplete compliance (edit to change scope).
export const INCOMPLETE_COMPLIANCE_DOC_STATUSES: ReviewStatus[] = [
  REVIEW_STATUS_OPTIONS_MAP.VALIDATION_FAILED, // Errors detected
  REVIEW_STATUS_OPTIONS_MAP.SENT_TO_AGENT_FOR_UPDATES, // Returned to agent
  REVIEW_STATUS_OPTIONS_MAP.READY_FOR_USER_REVIEW, // Ready for user review
];

// Agent-side draft statuses (`reviewStatus`): the doc hasn't been submitted for
// processor review yet, so compliance views must not show it. Mirrors the
// backend's PRE_SUBMISSION_STATUSES.
export const PRE_SUBMISSION_REVIEW_STATUSES: readonly ReviewStatus[] = [
  REVIEW_STATUS_OPTIONS_MAP.NOT_STARTED,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_REVIEWING,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_ERRORS_FOUND,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_APPROVED,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_READY_FOR_REVIEW,
];

/**
 * `displayStatus` values that qualify for submitting documents to Max (agent) review.
 * Keep in sync with bulk actions and verify-document Max Review CTA.
 */
export const ELIGIBLE_AGENT_MAX_REVIEW_STATUSES: readonly ReviewStatus[] = [
  REVIEW_STATUS_OPTIONS_MAP.NOT_APPLICABLE,
  REVIEW_STATUS_OPTIONS_MAP.NOT_STARTED,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_REVIEWING,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_ERRORS_FOUND,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_APPROVED,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_READY_FOR_REVIEW,
];

/**
 * `displayStatus` values that qualify for submitting documents to processor (compliance) review.
 * AGENT_MAX_ERRORS_FOUND is submittable after a "submit anyway" confirmation.
 * SENT_TO_AGENT_FOR_UPDATES lets returned docs go back to processor review
 * without another Max pass.
 * NOT_STARTED lets a freshly uploaded doc go straight to the processor, skipping Max.
 * TODO: Should NOT_APPLICABLE be included?
 */
export const ELIGIBLE_PROCESSOR_REVIEW_STATUSES: readonly ReviewStatus[] = [
  REVIEW_STATUS_OPTIONS_MAP.NOT_STARTED,
  REVIEW_STATUS_OPTIONS_MAP.NOT_APPLICABLE,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_APPROVED,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_ERRORS_FOUND,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_READY_FOR_REVIEW,
  REVIEW_STATUS_OPTIONS_MAP.SENT_TO_AGENT_FOR_UPDATES,
];

export const EXTRACTION_TYPE_OPTIONS: Record<string, string> = {
  CLASSIFIER_ONLY: 'CLASSIFIER_ONLY',
  EXTRACTION: 'EXTRACTION',
};

/**
 * Status sequence for document sorting/grouping.
 * Documents are sorted in this order (lower index = appears first).
 * Statuses not in this array will appear last.
 */
export const DOCUMENT_STATUS_SORT_SEQUENCE = [
  // 1. Errors Detected
  REVIEW_STATUS_OPTIONS_MAP.VALIDATION_FAILED,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_ERRORS_FOUND,
  // 2. Ready for user review
  REVIEW_STATUS_OPTIONS_MAP.READY_FOR_USER_REVIEW,
  // 3. Returned to agent
  REVIEW_STATUS_OPTIONS_MAP.SENT_TO_AGENT_FOR_UPDATES,
  // 4. Max Approved
  REVIEW_STATUS_OPTIONS_MAP.MAX_APPROVED,
  // 5. Approved
  REVIEW_STATUS_OPTIONS_MAP.USER_APPROVED,
  // 6. Not Applicable
  REVIEW_STATUS_OPTIONS_MAP.NOT_APPLICABLE,
  // 7. Pending Max Review
  REVIEW_STATUS_OPTIONS_MAP.PROCESSING,
  REVIEW_STATUS_OPTIONS_MAP.NOT_STARTED,
  REVIEW_STATUS_OPTIONS_MAP.AGENT_MAX_REVIEWING,
];

// Mirrors EXTRACTION_SOURCE in the backend's src/config/globals.js.
export const EXTRACTION_SOURCE = {
  EXTRACTION: 'EXTRACTION',
  TEMPLATE: 'TEMPLATE',
} as const;

// Not `extraction.status === 'NOT_APPLICABLE'`: 150k classified uploads carry that too.
export const isMaxGeneratedDocument = (
  doc: { extractionSource?: string } | null | undefined
): boolean => doc?.extractionSource === EXTRACTION_SOURCE.TEMPLATE;

// Types the shared map has no name for. Kept out of documentNameMap itself: 30-odd files read that
// map, and some prefer a mapped name over the document's own title.
const EXTRA_TYPE_LABELS: Record<string, string> = {
  USER_UPLOADED_MULTI_DOC: 'Multi-Document Upload',
};

// Record-level rules for retyping. Surfaces add their own view rules (archived, wrong tab).
export const isDocumentTypeChangeable = (
  doc:
    | { type?: string; extractionSource?: string; deletedAt?: string }
    | null
    | undefined
): boolean =>
  !!doc &&
  !doc.deletedAt &&
  doc.type !== 'USER_UPLOADED_MULTI_DOC' &&
  // Max-generated docs take their type from the form; there is no classification to redo.
  !isMaxGeneratedDocument(doc);

// Max read the pages but could not name the document, so compliance checks cannot run on it.
export const isUnidentifiedDocument = (
  doc: { type?: string; extractionSource?: string } | null | undefined
): boolean =>
  !!doc &&
  !isMaxGeneratedDocument(doc) &&
  (!doc.type || doc.type === 'UNKNOWN');

// Brokerage label first, then the shared map, then the raw code; brokerage lists often omit a doc's own type.
export const documentTypeLabel = (type: string, brokerageLabel?: string) => {
  const mapped =
    brokerageLabel || documentNameMap[type] || EXTRA_TYPE_LABELS[type];
  return mapped && mapped !== UNKNOWN_DOCUMENT_NAME ? mapped : type;
};
